import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Project, Session } from '@claude-code-api/shared';
import { ChevronRight } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { ProjectCard } from '@/components/ProjectCard';
import { SectionError } from '@/components/dashboard/SectionError';
import { EmptyProjects } from '@/components/dashboard/EmptyProjects';

interface RecentProjectsProps {
  projects: Project[];
  sessions: Session[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onStartSession: (projectId: string) => void;
  onProjectCreated: (project: Project) => void;
}

function RecentProjectsSkeleton() {
  return (
    <div>
      <Skeleton className="h-7 w-40 mb-4" />
      <div className="grid gap-4 md:grid-cols-2">
        <Skeleton className="h-40 rounded-lg" />
        <Skeleton className="h-40 rounded-lg" />
        <Skeleton className="h-40 rounded-lg" />
        <Skeleton className="h-40 rounded-lg" />
      </div>
    </div>
  );
}

export function RecentProjects({
  projects,
  sessions,
  loading,
  error,
  onRetry,
  onStartSession,
  onProjectCreated,
}: RecentProjectsProps) {
  const { t } = useTranslation('dashboard');

  if (loading) {
    return <RecentProjectsSkeleton />;
  }

  if (error) {
    return <SectionError message={error} onRetry={onRetry} />;
  }

  if (projects.length === 0) {
    return <EmptyProjects onProjectCreated={onProjectCreated} />;
  }

  return (
    <div>
      <h2 className="text-lg font-semibold font-heading mb-4">
        {t('recentProjectsTitle')}
      </h2>
      <div className="grid gap-4 md:grid-cols-2">
        {projects.slice(0, 6).map((project) => {
          const activeSession = sessions.find(
            (s) => s.projectId === project.id && s.status === 'running',
          );
          return (
            <ProjectCard
              key={project.id}
              project={project}
              activeSession={activeSession}
              onStart={onStartSession}
            />
          );
        })}
      </div>
      {projects.length > 6 && (
        <Link
          to="/projects"
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mt-4"
        >
          {t('viewAllProjects')}
          <ChevronRight className="w-4 h-4" />
        </Link>
      )}
    </div>
  );
}
